<template>
  <div>
    <div class="card col-md-12">
      <div class="card-header" style="margin-bottom: 10px">
        <h4 class="card-title" style="display:inline">
          Search
        </h4>
      </div>
      <div class="card-body form-group">
        <div class="row">
          <div class="col-md-6">
            <label>
              Data Type &nbsp;(
                <el-button @click="selectAll" type="text" size="mini" plain style="top:-5px; padding: 0;">
                  Select All
                </el-button>
              )
            </label>
            <el-select
                class="select"
                size="large"
                multiple
                filterable
                clearable
                data-vv-name="dataType"
                v-validate="modelValidations.dataType"
                placeholder="Search by Entity Type"
                v-model="query.entityTypes">
              <el-option
                  v-for="option in entityOptions"
                  class="select"
                  :value="option.value"
                  :label="option.label"
                  :key="option.value">
              </el-option>
            </el-select>
            <small class="text-danger" style="display: block" v-show="errors.has('dataType')">
              At least one data type is required.
            </small>
          </div>
          <div class="col-md-2">
            <div class="row">&nbsp</div>
            <div class="row">
              <el-checkbox v-model="query.queryByCreatedAt" v-validate="!query.queryByUpdatedAt ? 'required:true' : ''" data-vv-name="created" style="margin:auto;">
                Created
              </el-checkbox>
            </div>
            <div class="row">
              <el-checkbox v-model="query.queryByUpdatedAt" v-validate="!query.queryByCreatedAt ? 'required:true' : ''" data-vv-name="updated" style="margin:auto; left:14px;">
                Updated
                <el-tooltip class="item" effect="dark" content="Only the latest update will show up in results." placement="bottom-start">
                  <img class="icon-info m-left-4" src="static/img/icon-info.png" />
                </el-tooltip>
              </el-checkbox>
            </div>
            <small class="text-danger" style="display: block" v-show="errors.has('created') && errors.has('updated')">
              At least one action is required.
            </small>
          </div>
          <div class="col-md-4">
            <div class="form-group">
              <label>Date Range</label>
              <el-date-picker
                  type="daterange"
                  align="right"
                  unlink-panels
                  range-separator="-"
                  start-placeholder="Start date"
                  end-placeholder="End date"
                  v-model="query.dateRange"
                  data-vv-name="dateRange"
                  v-validate="modelValidations.dateRange"
                  :format="'MMM dd, yyyy'"
                  :value-format="constants.DATETIME.URL_FORMAT.VALUE"
                  :picker-options="datePickerConfig.dateRange"
                  :default-time="defaultTime"
                  placeholder="Change Date">
              </el-date-picker>
              <small class="text-danger" v-show="errors.has('dateRange')">
                The date range fields are required.
              </small>
            </div>
          </div>
        </div>
      </div>
      <div class="col-md-12">
        <button type="button" @click="searchSubmit" class="btn btn-fill btn-info pull-right btn-search">
          Search
        </button>
        <button type="button" @click="clear" class="btn btn-fill btn-info pull-right btn-search">
          Clear
        </button>
      </div>
    </div>
    <div class="card">
      <div class="card-body">
        <div class="row">
          <div class="col-md-12">
            <h5 class="card-title" style="display:inline; margin:10px;">PTRAC History for {{username}}</h5>
            <el-table
                class="wrap-whole-words"
                :data="tableData"
                :max-height="tableHeight(400)"
                style="width: 100%">
              <el-table-column
                width="200"
                prop="dataType"
                label="Data Type">
                <template slot-scope="props">
                  {{ entityOptions.find(option => { return option.value === props.row.entityType }).label }}
                </template>
              </el-table-column>
              <el-table-column
                min-width="200"
                prop="name"
                label="Name">
                <template slot-scope="props">
                  <a href="#" @click.prevent="handleClickName(props.row)">
                    <span v-if="props.row.entityType === constants.ENTITIES.PRICE_RULE"> {{ props.row.shortId }} - {{ props.row.price | formatPrice }}</span>
                    <span v-else-if="props.row.entityType === constants.ENTITIES.INVOICE"> {{ props.row.customerName }} - {{ props.row.refNumber }}</span>
                    <span v-else-if="props.row.entityType === constants.ENTITIES.ITEM_CODE"> {{ props.row.name }} - {{ props.row.qbClass }}</span>
                    <span v-else-if="props.row.entityType === constants.ENTITIES.TAG"> {{ props.row.name }} - {{ props.row.projectType }}</span>
                    <span v-else>{{ props.row.name }}</span>
                  </a>
                </template>
              </el-table-column>
              <el-table-column
                width="100"
                prop="createdOrUpdated"
                label="Action">
                <template slot-scope="props">
                  <div v-if="props.row.updatedAt">
                    Updated
                  </div>
                  <div v-else>
                    Created
                  </div>
                </template>
              </el-table-column>
              <el-table-column
                width="250"
                prop="timestamp"
                label="Timestamp">
                <template slot-scope="props">
                  <div v-if="props.row.updatedAt">
                    {{ props.row.updatedAt | formatDateTime}}
                  </div>
                  <div v-else>
                    {{ props.row.createdAt | formatDateTime}}
                  </div>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </div>
        <div class="row">
          <div class="col-sm-6 pagination-info">
            <span class="category">Showing {{total}} entries</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {sync} from 'vuex-pathify'
import Constants from 'src/utilities/constants'
import {mapGetters} from 'vuex'
import {getTableHeight, notifyError} from 'src/utilities/utils'
import {OK} from 'http-status-codes'
import {fetchUser} from '@/utilities/usersApi'
import {capitalCase} from 'change-case'
import datePickerConfig from '@/components/UIComponents/DatePicker/datePickerConfig'

export default {
  props: ['data'],
  computed: {
    query: sync('history@query'),
    ...mapGetters({
      username: 'security/name',
      userId: 'security/userId',
      tableData: 'history/results'
    }),
    entityOptions() {
      let entities = []
      for (const entity of this.entityTypes) {
        if (entity === 'PTRAC_TASK') {
          entities.push({
            label: 'Task',
            value: Constants.ENTITIES[entity]
          })
        } else if (entity === 'PTRAC_FILE') {
          entities.push({
            label: 'File',
            value: Constants.ENTITIES[entity]
          })
        } else {
          entities.push({
            label: capitalCase(Constants.ENTITIES[entity]),
            value: Constants.ENTITIES[entity]
          })
        }
      }
      return entities
    },
    total() {
      return this.tableData.length
    }
  },
  data() {
    return {
      constants: Constants,
      datePickerConfig,
      defaultTime: ['00:00:00.000','23:59:59.999'],
      entityTypes: ['CAMUNDA_DEPLOYMENT', 'CITY', 'COMMUNITY', 'COMPANY', 'PTRAC_FILE',
                    'INVOICE', 'ITEM_CODE', 'JOB', 'JOB_TEMPLATE', 'LEGAL_SUBDIVISION', 'METADATUM', 'PLAN_NAME', 'PRICE_RULE',
                    'PROJECT', 'STREET', 'TAG', 'PTRAC_TASK', 'TASK_NAME', 'TASK_TEMPLATE', 'USER'],
      modelValidations: {
        dataType: {
          required: true
        },
        dateRange: {
          required: true
        }
      }
    }
  },
  methods: {
    selectAll() {
      const selectedEntities = []
      for (const entity of this.entityTypes) {
        selectedEntities.push(Constants.ENTITIES[entity])
      }
      this.$store.set('history/query@entityTypes', selectedEntities)
    },
    async searchSubmit(clickEvent) {
      if (await this.$validator.validateAll()) {
        const response = await this.$store.dispatch('history/fetchResults')

        if (OK !== response.status) {
          notifyError(this, 'Unable to perform history search')
          return
        }

        if (response.data.length === 0 && clickEvent) { // Prevents modal from showing on mount.
          this.$eventHub.$emit('show-modal-1', 'No Results Found', null, '500px')
        }
      }
    },
    async handleClickName(entity){
      switch(entity.entityType) {
        case Constants.ENTITIES.CAMUNDA_DEPLOYMENT: {
          return this.$eventHub.$emit('show-modal-1', "Process Definition", entity, '90%')
        }
        case Constants.ENTITIES.CITY: {
          return this.$eventHub.$emit('show-modal-1', "City", {id: entity.id}, '700px')
        }
        case Constants.ENTITIES.COMMUNITY: {
          return this.$eventHub.$emit('show-modal-1', 'Community', {id: entity.id}, '500px')
        }
        case Constants.ENTITIES.COMPANY: {
          return this.$eventHub.$emit('show-modal-2', "Company", {id: entity.id}, '1000px')
        }
        case Constants.ENTITIES.PTRAC_FILE: {
          return this.$eventHub.$emit('show-modal-1', 'File Details', entity, '600px')
        }
        case Constants.ENTITIES.INVOICE: {
          return this.$eventHub.$emit('show-modal-1', 'View Invoice', {id: entity.id}, '95%')
        }
        case Constants.ENTITIES.ITEM_CODE: {
          return this.$eventHub.$emit('show-modal-1', 'Item Code', {id: entity.id}, '600px')
        }
        case Constants.ENTITIES.JOB: {
          return this.$eventHub.$emit('show-modal-1', "Edit Job", {id: entity.id}, '80%')
        }
        case Constants.ENTITIES.JOB_TEMPLATE: {
          return this.$eventHub.$emit('show-modal-2', "Job Template", {id: entity.id}, '800px')
        }
        case Constants.ENTITIES.LEGAL_SUBDIVISION: {
          return this.$eventHub.$emit('show-modal-1', 'Legal Subdivision', {id: entity.id}, '500px')
        }
        case Constants.ENTITIES.METADATUM: {
          await this.$store.dispatch('metadata/fetchByEntityAndEntityId', {entity: entity.entityType, entityId: entity.entityId})
          return this.$eventHub.$emit('show-modal-1', 'Metadata', {entity: entity.entityType, entityId: entity.entityId}, '900px')
        }
        case Constants.ENTITIES.PLAN_NAME: {
          return this.$eventHub.$emit('show-modal-3', 'Plan Name', {id: entity.id}, '500px')
        }
        case Constants.ENTITIES.PRICE_RULE: {
          return this.$eventHub.$emit('show-modal-1', "Price Rule", {id: entity.id}, '1200px')
        }
        case Constants.ENTITIES.PROJECT: {
          return await this.$router.push(`/projects/${entity.id}`)
        }
        case Constants.ENTITIES.STREET: {
          return this.$eventHub.$emit('show-modal-5', 'Street', {id: entity.id}, '500px')
        }
        case Constants.ENTITIES.TAG: {
          return this.$eventHub.$emit('show-modal-1', 'Tag', {id: entity.id}, '500px')
        }
        case Constants.ENTITIES.TASK: {
          return this.$eventHub.$emit('show-modal-2', 'Edit Task', {id: entity.id}, '1000px')
        }
        case Constants.ENTITIES.TASK_TEMPLATE: {
          return this.$eventHub.$emit('show-modal-1', "Task Template", {id: entity.id}, '1100px')
        }
        case Constants.ENTITIES.TASK_NAME: {
          return this.$eventHub.$emit('show-modal-1', "Task Name", {id: entity.id}, '600px')
        }
        case Constants.ENTITIES.USER: {
          const response = await fetchUser(entity.id)
          const ptracUser = response.data
          return this.$eventHub.$emit('show-modal-1', 'Edit User', ptracUser, '1000px')
        }
      }
    },
    tableHeight(buffer) {
      return getTableHeight(buffer)
    },
    clear() {
      this.$store.commit('history/resetQuery')
      this.$store.commit('history/resetResults')
      this.$store.set('history/query@userId', this.userId)
    }
  },
  beforeMount() {
    this.$store.commit('history/resetResults')
    this.$store.set('history/query@userId', this.userId)
  },
  mounted() {
    this.searchSubmit()
  }
}
</script>
